import React, { JSX } from "react";
import { Button } from "components/common/Button";
import { InfoHint } from "components/common/InfoHint";
import { Inline } from "components/common/Stack";

/**
 * A quiet toggle at the end of a rule ("Show all", "Collapse all"). Callers
 * pass intent, not styling, so every section action looks the same.
 */
export interface SectionRuleAction {
  label: string;
  onClick: () => void;
  /** Set when the action shows or hides the section's body. */
  expanded?: boolean;
  /** Forwarded to the button for tests. */
  testId?: string;
  disabled?: boolean;
  loading?: boolean;
  /** For a caller that hands focus back to the action. */
  ref?: React.Ref<HTMLButtonElement>;
  /** The id of the region the action opens. */
  controls?: string;
}

/**
 * The project's one section header: label, a hairline filling the remaining
 * width, and optional trailing count and/or action.
 *
 * Page sections lead with an `icon` and set `inset`, which lines the label up
 * with the `px-1` rows underneath; dialog sections take neither.
 */
export function SectionRule({
  label,
  info,
  icon,
  count,
  trailing,
  action,
  end,
  inset = false,
}: {
  label: string;
  /// What this section is for, on a hover mark against the label.
  info?: string;
  icon?: React.ReactNode;
  /**
   * A count that belongs to the heading rather than to the rule — rendered
   * against the label, before the hairline. Same mono/tabular treatment as
   * `trailing`; use one or the other, not both.
   */
  count?: React.ReactNode;
  /**
   * Rendered mono with tabular figures, so counts don't jitter as they change.
   * One step down the scale from the label: Geist Mono has a taller x-height
   * than Geist Sans, so matching the px size makes the count read larger than
   * the heading it sits beside.
   */
  trailing?: React.ReactNode;
  action?: SectionRuleAction;
  /** Arbitrary control at the far end, unstyled — e.g. a dashed add pill. */
  end?: React.ReactNode;
  inset?: boolean;
}): JSX.Element {
  return (
    <Inline
      gap="sm"
      align="center"
      className={inset ? "item-height px-1" : "item-height"}
      style={{ marginBottom: "var(--spacing-sm)" }}
    >
      {icon && (
        <span className="flex w-5 shrink-0 items-center justify-center text-fg-muted">
          {icon}
        </span>
      )}
      <span className="text-fg-muted-bold cursor-default select-none">
        {label}
      </span>
      {info && <InfoHint title={info} label={`About ${label}`} />}
      {count !== undefined && count !== null && count !== 0 && (
        <span
          className="font-mono text-fg-muted"
          style={{
            fontSize: "var(--font-size-small)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {count}
        </span>
      )}
      <span
        style={{
          flex: 1,
          height: "var(--size-border-hairline)",
          background: "var(--color-border)",
        }}
      />
      {trailing && (
        <span
          className="font-mono text-fg-muted"
          style={{
            fontSize: "var(--font-size-small)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {trailing}
        </span>
      )}
      {action && (
        <Button
          variant="link"
          size="xs"
          className="section-rule-action"
          ref={action.ref}
          onClick={action.onClick}
          disabled={action.disabled}
          loading={action.loading}
          aria-expanded={action.expanded}
          aria-controls={action.controls}
          data-testid={action.testId}
        >
          {action.label}
        </Button>
      )}
      {end}
    </Inline>
  );
}
